import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  getAiUseCasePromptConfig,
  updateAiPromptConfig,
  updateAiUseCasePromptConfig,
} from '../../admin/services/aiPromptConfigService';
import { AiPromptConfig, AiPromptUseCaseId, usesDedicatedPromptOverride } from '../../admin/types/AiPromptConfig';
import { generateSetupInstructions, visibleSetupInstruction } from '../setupInstructionDraft';
import { SetupInstructionKey } from '../setupModel';
import styles from './WorkspaceSetupPage.module.css';

export interface SetupInstructionHandle {
  /** Saves edited instructions. Returns false when the step should stay put. */
  saveIfNeeded: () => Promise<boolean>;
}

interface SetupInstructionStepProps {
  instructionKey: SetupInstructionKey;
  useCaseId: AiPromptUseCaseId;
  canManage: boolean;
}

export const SetupInstructionStep = React.forwardRef<SetupInstructionHandle, SetupInstructionStepProps>(
  ({ instructionKey, useCaseId, canManage }, ref) => {
    const { t, i18n } = useTranslation('workspaces');
    const [config, setConfig] = React.useState<AiPromptConfig | null>(null);
    const [businessInfo, setBusinessInfo] = React.useState('');
    const [prompt, setPrompt] = React.useState('');
    const [tone, setTone] = React.useState('');
    const [baseline, setBaseline] = React.useState({ prompt: '', tone: '' });
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
      let cancelled = false;
      setLoading(true);
      setError(null);

      void getAiUseCasePromptConfig(useCaseId)
        .then(next => {
          if (cancelled) {
            return;
          }

          const nextPrompt = visibleSetupInstruction(next.systemPrompt, next.isDefault);
          const nextTone = next.toneOfVoice ?? '';
          setConfig(next);
          setPrompt(nextPrompt);
          setTone(nextTone);
          setBaseline({ prompt: nextPrompt, tone: nextTone });
        })
        .catch(loadError => {
          if (!cancelled) {
            setError(loadError instanceof Error ? loadError.message : t('setup.instructions.loadFailed'));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
    }, [t, useCaseId]);

    React.useImperativeHandle(ref, () => ({
      saveIfNeeded: async () => {
        const dirty = prompt !== baseline.prompt || tone !== baseline.tone;
        if (!dirty) {
          return true;
        }

        if (!prompt.trim()) {
          setError(t('setup.instructions.promptRequired'));
          return false;
        }

        if (!canManage) {
          setError(t('setup.instructions.noPermission'));
          return false;
        }

        if (!config) {
          setError(t('setup.instructions.loadFailed'));
          return false;
        }

        setSaving(true);
        setError(null);
        try {
          const request = {
            systemPrompt: prompt.trim(),
            toneOfVoice: tone.trim() || null,
            includeCompanyKnowledge: config.includeCompanyKnowledge,
            includeConversationHistory: config.includeConversationHistory,
            conversationHistoryMessageCount: config.conversationHistoryMessageCount,
            conversationHistoryTodayOnly: config.conversationHistoryTodayOnly,
            maxTokensResponse: config.maxTokensResponse,
            customFields: config.customFields,
            changeReason: 'Workspace setup',
          };
          // The shared customer prompt is the reply template. Channel and comment
          // steps store a dedicated override; the shared step cannot.
          const updated = usesDedicatedPromptOverride(useCaseId)
            ? await updateAiUseCasePromptConfig(useCaseId, request)
            : await updateAiPromptConfig(config.templateType, request);
          const nextPrompt = updated.systemPrompt ?? prompt.trim();
          const nextTone = updated.toneOfVoice ?? '';
          setConfig(updated);
          setBaseline({ prompt: nextPrompt, tone: nextTone });
          return true;
        } catch (saveError) {
          setError(saveError instanceof Error ? saveError.message : t('setup.instructions.saveFailed'));
          return false;
        } finally {
          setSaving(false);
        }
      },
    }), [baseline.prompt, baseline.tone, canManage, config, prompt, t, tone, useCaseId]);

    const generate = () => {
      const info = businessInfo.trim();
      if (!info) {
        setError(t('setup.instructions.businessRequired'));
        return;
      }

      setError(null);
      setPrompt(generateSetupInstructions(
        info,
        i18n.resolvedLanguage || i18n.language,
        t(`setup.instructions.${instructionKey}.scope`),
      ));
    };

    return (
      <div className={styles.instruction}>
        <p className={styles.hint}>{t(`setup.instructions.${instructionKey}.hint`)}</p>
        {loading ? <p className={styles.hint}>{t('setup.instructions.loading')}</p> : null}
        {error ? <p className={styles.error}>{error}</p> : null}
        <label className={styles.field}>
          <span>{t('setup.instructions.businessLabel')}</span>
          <textarea
            className={`${styles.textarea} ${styles.businessInfo}`}
            value={businessInfo}
            onChange={event => setBusinessInfo(event.target.value)}
            placeholder={t('setup.instructions.businessPlaceholder')}
            rows={4}
            disabled={!canManage || loading || saving}
          />
        </label>
        <div className={styles.generateRow}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={generate}
            disabled={!canManage || loading || saving || !businessInfo.trim()}
          >
            {t('setup.instructions.generate')}
          </button>
          <p className={styles.hint}>{t('setup.instructions.generateHint')}</p>
        </div>
        <label className={styles.field}>
          <span>{t('setup.instructions.promptLabel')}</span>
          <textarea
            className={styles.textarea}
            value={prompt}
            onChange={event => setPrompt(event.target.value)}
            placeholder={t(`setup.instructions.${instructionKey}.placeholder`)}
            rows={8}
            disabled={!canManage || loading || saving}
          />
        </label>
        <label className={styles.field}>
          <span>{t('setup.instructions.toneLabel')}</span>
          <input
            className="text-input"
            value={tone}
            onChange={event => setTone(event.target.value)}
            placeholder={t('setup.instructions.tonePlaceholder')}
            disabled={!canManage || loading || saving}
          />
        </label>
        <p className={styles.hint}>{t('setup.instructions.skipKeepsDefault')}</p>
      </div>
    );
  },
);

SetupInstructionStep.displayName = 'SetupInstructionStep';
